import React, { useState, useMemo, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpen, ClipboardCheck, Award, FileSpreadsheet, Plus, X,
  GraduationCap, Users, Presentation, Home, Newspaper, Calculator,
  PlayCircle, ShieldAlert, FolderOpen, Activity, HelpCircle, BookText,
  Info, LogIn, UserPlus, LogOut, User as UserIcon, Settings, Wrench,
  TrendingUp, Trophy, MessageCircle, CalendarDays, MapPin, Clapperboard, Film,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { haptic } from '../lib/paliers';
import { InstallPwaButton } from './InstallPwaButton';

// =====================================================================
// MobileBottomNav — barre de navigation fixe en bas, visible < 768px.
// 4 onglets contextuels + 1 onglet "Plus" qui ouvre un drawer.
// L'ensemble s'adapte au rôle de l'utilisateur (visiteur / élève / enseignant).
// =====================================================================

const NAV_VISITOR = [
  { to: '/', label: 'Accueil', icon: Home, testid: 'mnav-home' },
  { to: '/cours/progression', label: 'Progression', icon: TrendingUp, testid: 'mnav-progression' },
  { to: '/shorts', label: 'Shorts', icon: Clapperboard, testid: 'mnav-shorts', special: true },
  { to: '/cours-interactifs', label: 'Cours', icon: BookOpen, testid: 'mnav-cours' },
  { to: '/magazine', label: 'Actu', icon: Newspaper, testid: 'mnav-magazine' },
];

const NAV_ELEVE = [
  { to: '/eleve/dashboard', label: 'Accueil', icon: GraduationCap, testid: 'mnav-dashboard' },
  { to: '/mon-planning', label: 'Planning', icon: CalendarDays, testid: 'mnav-planning-eleve' },
  { to: '/shorts', label: 'Shorts', icon: Clapperboard, testid: 'mnav-shorts-eleve', special: true },
  { to: '/livret', label: 'Livret', icon: ClipboardCheck, testid: 'mnav-livret' },
  { to: '/messages', label: 'Messages', icon: MessageCircle, testid: 'mnav-messages-eleve' },
];

const NAV_ENSEIGNANT = [
  { to: '/enseignant/planning', label: 'Ma journée', icon: CalendarDays, testid: 'mnav-planning' },
  { to: '/enseignant/dashboard', label: 'Élèves', icon: Users, testid: 'mnav-eleves' },
  { to: '/shorts', label: 'Shorts', icon: Clapperboard, testid: 'mnav-shorts-enseignant', special: true },
  { to: '/enseignant/examen-blanc-grille', label: 'Bilans', icon: ClipboardCheck, testid: 'mnav-bilans' },
  { to: '/enseignant/messages', label: 'Messages', icon: MessageCircle, testid: 'mnav-messages' },
];

// "Plus" drawer items — grouped by section, contextuels au rôle
function getPlusItems(role, user) {
  const common = [
    { to: '/shorts', label: 'Shorts · leçons en 30 s', icon: Clapperboard, desc: 'Le format vertical à swiper' },
    { to: '/videos', label: 'Vidéos de la communauté', icon: Film, desc: 'Galerie validée par l\'équipe' },
    { to: '/annuaire', label: 'Annuaire des auto-écoles', icon: MapPin, desc: '11 196 écoles · carte de France' },
    { to: '/livret-am/lecteur', label: 'Livret AM (flipbook)', icon: BookText, desc: 'Voiturette · 67 pages à feuilleter' },
    { to: '/magazine', label: 'Magazine', icon: Newspaper, desc: '26 dossiers & enquêtes' },
    { to: '/simulateurs', label: 'Simulateurs', icon: Calculator, desc: 'Coût · Points · Distance' },
    { to: '/onisr', label: 'Baromètre ONISR', icon: Activity, desc: 'Stats officielles mises à jour' },
    { to: '/tutos', label: 'Tutos vidéo', icon: PlayCircle, desc: 'Manœuvres & sécurité' },
  ];
  const eleveOnly = [
    { to: '/cours/progression', label: 'Ma progression', icon: TrendingUp, desc: 'Suivi REMC détaillé' },
    { to: '/cours/eleve/voiture', label: 'Mes cours', icon: BookOpen, desc: '38 leçons REMC' },
    { to: '/cours/eleve/guide-aac', label: 'Guide AAC', icon: BookOpen, desc: 'Conduite accompagnée — l\'essentiel' },
    { to: '/cours/eleve/bilans', label: 'Mes bilans', icon: ClipboardCheck, desc: 'Bilans de compétences' },
    { to: '/cours/eleve/examen-blanc', label: 'Permis blanc', icon: Award, desc: 'Grille 31 critères' },
    { to: '/eleve/dashboard#dash-qcm-badges', label: 'Mes badges', icon: Trophy, desc: 'Ta progression gamifiée' },
    { to: '/cours/eleve/infractions-sanctions', label: 'Infractions & Sanctions', icon: ShieldAlert, desc: '8 panneaux interactifs' },
    { to: '/cours-interactifs', label: 'Tous les cours interactifs', icon: BookOpen, desc: 'Catalogue complet' },
    { to: '/formation/parcours', label: 'Parcours de formation', icon: GraduationCap, desc: 'B · AAC · CS · BEA' },
    { to: '/ressources', label: 'Base documentaire', icon: FolderOpen, desc: 'Fiches officielles' },
  ];
  const enseignantOnly = [
    { to: '/enseignant/mes-cours', label: 'Mes cours', icon: BookOpen, desc: 'Supports pédagogiques' },
    { to: '/enseignant/seances', label: 'Mes séances & RDV pédago', icon: Presentation, desc: 'AAC bilan mi-parcours · 8 modèles' },
    { to: '/cours/progression', label: 'Progression & comptes-rendus', icon: TrendingUp, desc: 'Suivi élèves + envoi WhatsApp' },
    { to: '/enseignant/cours-en-salle', label: 'Bibliothèque cours en salle', icon: Presentation, desc: 'Slides projetables en classe' },
    { to: '/enseignant/examen-blanc-grille', label: 'Grille examen 31 critères', icon: FileSpreadsheet, desc: 'Évaluation REMC' },
    { to: '/cours/eleve/infractions-sanctions', label: 'Infractions & Sanctions', icon: ShieldAlert, desc: 'Support de cours' },
    { to: '/cours-interactifs', label: 'Tous les cours interactifs', icon: BookOpen, desc: 'Catalogue complet' },
    { to: '/ressources', label: 'Base documentaire', icon: FolderOpen, desc: 'Fiches & documents' },
  ];
  if (role === 'enseignant' && (user?.secretariat_access || user?.is_admin)) {
    enseignantOnly.unshift({ to: '/secretariat', label: 'Secrétariat / Gérant', icon: Settings, desc: 'Planning · fiches · export DSR' });
  }
  const visitorOnly = [
    { to: '/eleve', label: 'Espace Élève', icon: GraduationCap, desc: 'B · AAC · CS · BEA' },
    { to: '/enseignant', label: 'Espace Enseignant', icon: Users, desc: 'Moniteur ECSR' },
    { to: '/cours/eleve/infractions-sanctions', label: 'Infractions & Sanctions', icon: ShieldAlert, desc: '8 panneaux interactifs' },
    { to: '/tutos', label: 'Tutos vidéo', icon: PlayCircle, desc: 'Manœuvres & sécurité' },
  ];
  const utility = [
    { to: '/faq', label: 'FAQ', icon: HelpCircle, desc: 'Questions fréquentes' },
    { to: '/glossaire', label: 'Glossaire ECSR', icon: BookText, desc: '31 termes du permis' },
    { to: '/a-propos', label: 'À propos', icon: Info, desc: 'Notre mission' },
  ];

  if (role === 'eleve') return { main: eleveOnly, common, utility };
  if (role === 'enseignant') return { main: enseignantOnly, common, utility };
  return { main: visitorOnly, common, utility };
}

function isActive(pathname, to) {
  if (to === '/') return pathname === '/';
  // Strip hash fragment (used for scroll-to-section links)
  const cleanTo = to.split('#')[0];
  return pathname === cleanTo || pathname.startsWith(cleanTo + '/');
}

function NavButton({ item, active, badgeCount = 0, onClick }) {
  const Icon = item.icon;
  const handleClick = (e) => {
    haptic(12);
    // Si l'URL cible contient un hash, on gère manuellement le scroll
    // (ScrollToTop.jsx réinitialise scrollY à chaque navigation et écrase le hash natif)
    if (item.to.includes('#')) {
      const hash = item.to.split('#')[1];
      // Attend le prochain tick pour laisser React finir sa nav
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
    if (onClick) onClick(e);
  };
  if (item.special) {
    return (
      <Link
        to={item.to}
        data-testid={item.testid}
        onClick={handleClick}
        aria-current={active ? 'page' : undefined}
        aria-label={item.label}
        className="flex-1 flex flex-col items-center justify-center gap-1 h-full active:scale-95 transition-all"
      >
        <div className="relative -mt-7">
          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 px-2.5 py-[3px] rounded-full bg-white text-red-600 text-[9px] font-black uppercase tracking-[0.12em] leading-none shadow-md">
            New
          </span>
          <div className={`w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-xl shadow-red-900/60 transition-transform ${
            active ? 'bg-red-500 scale-105' : 'bg-gradient-to-b from-red-600 to-red-700'
          }`}>
            <Icon size={28} strokeWidth={2.2} className="text-white" />
          </div>
        </div>
        <span className={`text-[11.5px] leading-none font-black uppercase tracking-[0.14em] ${active ? 'text-red-300' : 'text-white/90'}`}>{item.label}</span>
      </Link>
    );
  }
  return (
    <Link
      to={item.to}
      data-testid={item.testid}
      onClick={handleClick}
      aria-current={active ? 'page' : undefined}
      aria-label={item.label}
      className={`flex-1 flex flex-col items-center justify-center gap-1 h-full transition-all active:scale-95 ${
        active ? 'text-red-400' : 'text-white/55 hover:text-white/80 active:text-white'
      }`}
    >
      <div className={`relative ${active ? 'scale-110' : ''} transition-transform`}>
        <Icon size={20} strokeWidth={active ? 2.5 : 2} />
        {badgeCount > 0 && (
          <span className="absolute -top-1.5 -right-2.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold inline-flex items-center justify-center">{badgeCount > 9 ? '9+' : badgeCount}</span>
        )}
        {active && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-500" />}
      </div>
      <span className={`text-[10.5px] leading-none font-medium ${active ? 'text-red-300' : ''}`}>
        {item.label}
      </span>
    </Link>
  );
}

export default function MobileBottomNav() {
  const { user, openLogin, openRegister, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const role = user?.role || null;
  const canMessage = role === 'eleve' || role === 'enseignant';

  useEffect(() => {
    if (!canMessage) { setUnread(0); return; }
    let alive = true;
    const load = () => api.messagesThreads().then((d) => { if (alive) setUnread(d?.total_unread || 0); }).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [canMessage, location.pathname]);

  const navItems = useMemo(() => {
    if (role === 'eleve') return NAV_ELEVE;
    if (role === 'enseignant') return NAV_ENSEIGNANT;
    return NAV_VISITOR;
  }, [role]);

  const plus = useMemo(() => getPlusItems(role, user), [role, user]);

  const handleNav = (to) => {
    setDrawerOpen(false);
    navigate(to);
  };

  // Le back-office admin et le secrétariat ont leur propre navigation mobile
  if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/secretariat')) return null;

  return (
    <>
      {/* Spacer to push page content above the nav */}
      <div data-mnav-spacer className="md:hidden h-[68px] w-full" aria-hidden="true" />

      {/* Bottom nav bar — mobile only */}
      <nav
        data-testid="mobile-bottom-nav"
        aria-label="Navigation mobile"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 h-[68px] bg-[#0a0a0b]/95 backdrop-blur-lg border-t border-white/10 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-stretch h-full max-w-md mx-auto">
          {navItems.map((it) => (
            <NavButton key={it.to} item={it} active={isActive(location.pathname, it.to)} badgeCount={it.to.includes('messages') ? unread : 0} />
          ))}
          <button
            type="button"
            data-testid="mnav-plus"
            onClick={() => { haptic(20); setDrawerOpen(true); }}
            className={`flex-1 flex flex-col items-center justify-center gap-1 h-full transition-all active:scale-95 ${
              drawerOpen ? 'text-red-400' : 'text-white/55 hover:text-white/80 active:text-white'
            }`}
          >
            <Plus size={20} strokeWidth={drawerOpen ? 2.5 : 2} />
            <span className="text-[10.5px] leading-none font-medium">Plus</span>
          </button>
        </div>
      </nav>

      {/* Drawer "Plus" — full-screen sheet */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fadeIn"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          {/* Sheet */}
          <div
            data-testid="mnav-drawer"
            className="md:hidden fixed inset-x-0 bottom-0 z-50 bg-[#0d0d0e] border-t border-white/10 rounded-t-3xl max-h-[85vh] overflow-y-auto animate-slideUp pb-[env(safe-area-inset-bottom)] shadow-[0_-20px_60px_rgba(0,0,0,0.6)]"
            role="dialog"
            aria-modal="true"
          >
            {/* Header drawer */}
            <div className="sticky top-0 z-10 bg-[#0d0d0e] border-b border-white/5 px-5 pt-3 pb-4">
              <div className="w-10 h-1 rounded-full bg-white/15 mx-auto mb-3" />
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">
                    {role === 'eleve' ? 'Espace Élève' : role === 'enseignant' ? 'Espace Enseignant' : 'Code Voiture'}
                  </div>
                  <div className="font-serif-editorial text-white text-[22px] leading-tight mt-0.5">
                    {user ? `Bonjour ${user.name?.split(' ')[0] || ''}` : 'Tout Code Voiture'}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="mnav-drawer-close"
                  onClick={() => setDrawerOpen(false)}
                  className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 hover:bg-white/10"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Sections */}
            <div className="px-5 py-5 space-y-7">
              {/* Section 1 — Principal contextualisé */}
              <DrawerSection
                title={role === 'visitor' || !role ? 'Découvrir' : 'Mon espace'}
                items={plus.main}
                onNav={handleNav}
                activePath={location.pathname}
              />

              {/* Section 2 — Communs */}
              <DrawerSection
                title="Lecture & outils"
                items={plus.common}
                onNav={handleNav}
                activePath={location.pathname}
              />

              {/* Section 3 — Utilitaire */}
              <DrawerSection
                title="Aide & informations"
                items={plus.utility}
                onNav={handleNav}
                activePath={location.pathname}
                compact
              />

              {/* Installation PWA */}
              <div className="pt-2 border-t border-white/5">
                <InstallPwaButton onDone={() => setDrawerOpen(false)} />
              </div>

              {/* Section auth */}
              <div className="pt-2 border-t border-white/5">
                {user ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/10">
                      <div className="w-10 h-10 rounded-full bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400">
                        <UserIcon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-[13.5px] font-semibold truncate">{user.name}</div>
                        <div className="text-white/45 text-[11.5px] truncate">{user.email}</div>
                      </div>
                    </div>
                    {user.is_admin && (
                      <button
                        type="button"
                        data-testid="mnav-admin-btn"
                        onClick={() => handleNav('/admin')}
                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 text-white/80 hover:bg-white/[0.06]"
                      >
                        <Settings size={15} className="text-red-400" />
                        <span className="text-[13.5px] font-medium">Administration</span>
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="mnav-logout-btn"
                      onClick={() => { logout(); setDrawerOpen(false); navigate('/'); }}
                      className="w-full flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 text-white/80 hover:bg-red-950/30 hover:border-red-800/40"
                    >
                      <LogOut size={15} className="text-red-400" />
                      <span className="text-[13.5px] font-medium">Se déconnecter</span>
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      data-testid="mnav-login-btn"
                      onClick={() => { setDrawerOpen(false); openLogin(); }}
                      className="flex items-center justify-center gap-2 h-12 rounded-xl bg-white/[0.04] border border-white/10 text-white text-[13.5px] font-semibold"
                    >
                      <LogIn size={14} /> Connexion
                    </button>
                    <button
                      type="button"
                      data-testid="mnav-register-btn"
                      onClick={() => { setDrawerOpen(false); openRegister(); }}
                      className="flex items-center justify-center gap-2 h-12 rounded-xl bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold shadow-lg shadow-red-900/40"
                    >
                      <UserPlus size={14} /> Créer un compte
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function DrawerSection({ title, items, onNav, activePath, compact = false }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/45 font-bold mb-3">{title}</div>
      <div className={compact ? 'grid grid-cols-3 gap-2' : 'space-y-2'}>
        {items.map((it) => {
          const Icon = it.icon;
          const active = isActive(activePath, it.to);
          if (compact) {
            return (
              <button
                key={it.to}
                type="button"
                data-testid={`drawer-${it.to.replace(/\//g, '-')}`}
                onClick={() => onNav(it.to)}
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border transition ${
                  active ? 'border-red-500 bg-red-950/20' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
                }`}
              >
                <Icon size={16} className={active ? 'text-red-400' : 'text-white/70'} />
                <span className={`text-[11px] font-medium leading-tight text-center ${active ? 'text-red-300' : 'text-white/75'}`}>{it.label}</span>
              </button>
            );
          }
          return (
            <button
              key={it.to}
              type="button"
              data-testid={`drawer-${it.to.replace(/\//g, '-')}`}
              onClick={() => onNav(it.to)}
              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border transition text-left ${
                active ? 'border-red-500 bg-red-950/20' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
              }`}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                active ? 'bg-red-600/20 border border-red-700/40 text-red-300' : 'bg-white/[0.05] border border-white/10 text-white/65'
              }`}>
                <Icon size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[13.5px] font-semibold leading-tight ${active ? 'text-white' : 'text-white/85'}`}>{it.label}</div>
                <div className="text-white/45 text-[11.5px] mt-0.5 truncate">{it.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
